import { format, type Locale } from "date-fns";
import type { TFunction } from "i18next";
import { CalendarIcon, XIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	BACKWARD_PRESETS,
	DATE_PARAM_FORMAT,
	type DateRangeValue,
	EMPTY_DATE_RANGE,
	formatDayInput,
	type PresetKey,
	parseDayInput,
	presetRange,
	resolveActivePreset,
} from "@/lib/appointments/date-range-presets";
import { cn } from "@/lib/utils";

export type {
	DateRangeValue,
	PresetKey,
} from "@/lib/appointments/date-range-presets";
// The range arithmetic, the preset catalogues and the tie-breaking live in
// lib/appointments/date-range-presets, so they can be tested without rendering a calendar.
// This file owns only how that logic is presented.
export {
	BACKWARD_PRESETS,
	BIDIRECTIONAL_PRESETS,
	EMPTY_DATE_RANGE,
} from "@/lib/appointments/date-range-presets";

// The one place a stored range turns into words, shared with the active-filter pills so
// the wire format and the display format have a single definition — a second copy would
// keep rendering the old shape the day either of them changes. A one-sided range is a
// real selection, not an unfinished one, so it gets its own wording instead of an empty
// half, and a value the calendar cannot parse is shown as it came rather than hidden.
export const formatDayRangeLabel = (
	from: string,
	to: string,
	t: TFunction,
	locale: Locale,
): string => {
	const asDay = (value: string) => {
		const parsed = parseDayInput(value);
		return parsed ? format(parsed, "PP", { locale }) : value;
	};

	if (from && to) return `${asDay(from)} - ${asDay(to)}`;
	if (from)
		return t("drafts.filters.date_range.from_only", { date: asDay(from) });

	return t("drafts.filters.date_range.to_only", { date: asDay(to) });
};

export interface DateRangeFilterProps {
	value: DateRangeValue;
	onChange: (value: DateRangeValue) => void;
	placeholder: string;
	// Readonly because the list is a fixed catalogue the caller owns; this component only
	// ever reads it.
	presets?: readonly PresetKey[];
	className?: string;
}

export default function DateRangeFilter({
	value,
	onChange,
	placeholder,
	presets = BACKWARD_PRESETS,
	className,
}: DateRangeFilterProps) {
	const { t } = useTranslation("appointments");
	const { locale, tzDate } = useDateContext();
	const [open, setOpen] = useState(false);

	// "Today" is the application's today, not the browser's. An admin travelling — or
	// simply on a laptop still set to another zone — would otherwise get a preset a day
	// off from the range the server resolves.
	const today = useMemo(() => {
		const inAppZone = format(new Date(), DATE_PARAM_FORMAT, { in: tzDate });
		return parseDayInput(inAppZone) as Date;
	}, [tzDate]);

	const selectedRange = useMemo(() => {
		const from = parseDayInput(value.from);
		const to = parseDayInput(value.to);
		if (!from && !to) return undefined;

		// react-day-picker anchors a range on `from`. A to-only range is still a valid
		// filter server-side, so it is shown as a single highlighted day rather than
		// being dropped from the calendar entirely.
		return { from: from ?? to, to };
	}, [value.from, value.to]);

	// Ranked by intent rather than by the order these buttons happen to be rendered in, so
	// two presets describing the same days always light up the same one — see
	// MATCH_PRECEDENCE.
	//
	// Keyed on the two strings, not on `value` itself: callers build that object inline on
	// every render, so depending on its identity would re-run the arithmetic for all six
	// presets each time the parent re-renders without the range having changed.
	const activePreset = useMemo(
		() =>
			resolveActivePreset({ from: value.from, to: value.to }, today, presets),
		[presets, today, value.from, value.to],
	);

	const label = useMemo(() => {
		const from = parseDayInput(value.from);
		const to = parseDayInput(value.to);
		if (!from && !to) return null;

		if (activePreset) return t(`drafts.filters.date_range.${activePreset}`);

		return formatDayRangeLabel(value.from, value.to, t, locale);
	}, [activePreset, locale, t, value.from, value.to]);

	const handleSelectRange = useCallback(
		(range: { from?: Date; to?: Date } | undefined) => {
			onChange({
				from: formatDayInput(range?.from),
				to: formatDayInput(range?.to),
			});
		},
		[onChange],
	);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					className="justify-start px-3 font-normal text-left shadow-inner bg-input"
				>
					<CalendarIcon className="w-4 h-4 mr-2 shrink-0 text-muted-foreground/75" />
					<span
						className={cn(
							"truncate line-clamp-1",
							!label && "text-muted-foreground",
							className,
						)}
					>
						{label || placeholder}
					</span>
				</Button>
			</PopoverTrigger>

			<PopoverContent
				className="flex flex-col w-auto p-0 sm:flex-row"
				align="start"
			>
				{/* Wraps rather than scrolls on a narrow viewport: with six shortcuts the
				    off-screen ones would otherwise be invisible until scrolled, and a
				    shortcut nobody can see is not a shortcut. Once the list becomes a
				    column the longest label sets the width, so a longer translation widens
				    the column instead of spilling over the divider; the floor keeps the
				    shorter created-date list looking the same as it does today. */}
				<div className="flex flex-row flex-wrap gap-1 p-2 border-b max-w-[18rem] sm:flex-col sm:flex-nowrap sm:border-b-0 sm:border-r sm:max-w-none sm:w-max sm:min-w-[10rem]">
					{presets.map((key) => (
						<Button
							key={key}
							type="button"
							size="sm"
							variant={activePreset === key ? "secondary" : "ghost"}
							className="justify-start shrink-0"
							onClick={() => onChange(presetRange(key, today))}
						>
							{t(`drafts.filters.date_range.${key}`)}
						</Button>
					))}

					{/* Clearing rather than switching to some other range: a custom range is
					    whatever the admin draws next on the calendar, so the honest action
					    behind the label is to get the preset's range out of the way first. */}
					<Button
						type="button"
						size="sm"
						variant={
							!activePreset && (value.from || value.to) ? "secondary" : "ghost"
						}
						className="justify-start shrink-0"
						onClick={() => onChange(EMPTY_DATE_RANGE)}
					>
						{t("drafts.filters.date_range.custom")}
					</Button>
				</div>

				<div className="flex flex-col">
					<Calendar
						mode="range"
						defaultMonth={parseDayInput(value.from) || today}
						selected={selectedRange}
						onSelect={handleSelectRange}
						numberOfMonths={1}
					/>

					{(value.from || value.to) && (
						<Button
							type="button"
							size="sm"
							variant="ghost-destructive"
							className="m-2 mt-0"
							onClick={() => {
								onChange(EMPTY_DATE_RANGE);
								setOpen(false);
							}}
						>
							<XIcon className="w-4 h-4 mr-1" />
							{t("drafts.filters.date_range.clear")}
						</Button>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
